import React from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import SectionCard from "../../profiles/SectionCard";
import SectionSkeleton from "../../profiles/SectionSkeleton";

// Matches ViewersAlsoView's row avatar size — the two rail cards share a visual rhythm.
const OUTLET_LOGO_SIZE = 45;

/**
 * MediaOutletsRailCard — chrome half of the media profile's "Media Outlets" rail card (the data
 * half is useMediaOutletsRail). A `<SectionCard>` with a stack of outlet rows: round logo +
 * linked outlet name + an optional "N requests" proxy-metric subtitle.
 *
 * `outlets` is the hook's union — the profile's public employment list first, then the outlets
 * an entitled viewer's RPC read says this reporter posts for — with `requestCount` present only
 * on rows that read matched. A row renders a count only when it's a finite POSITIVE number —
 * never "0 requests" for a zero or absent count — since the binding contract is that a count only
 * ever appears as a meaningful, monetized fact; anything else (not known to this viewer, or
 * genuinely zero) is silence, not "0".
 *
 * Purely presentational, driven entirely by hook state, and self-collapsing: a skeleton while the
 * entitlement read is in flight, nothing at all once the union settles empty. Whether an empty
 * card collapses the whole rail is the page's call — it owns the rail column (see MediaProfile).
 *
 * @param {Object} props
 * @param {Array} props.outlets - Row items: `{ id, href, imageUrl, title, requestCount }`
 * @param {boolean} [props.isLoading] - True while the entitlement-gated count lookup is in flight
 */
function MediaOutletsRailCard({ outlets, isLoading = false }) {
  if (isLoading) return <SectionSkeleton lines={5} />;
  if (!outlets.length) return null;

  return (
    <SectionCard id="media-outlets-rail" title="Contributes to">
      <div className="d-flex flex-column gap-3">
        {outlets.map((outlet) => (
          <div key={outlet.id} className="d-flex flex-row align-items-center">
            <ImageCropped
              roundedCircle
              wrapperClasses="me-2 flex-shrink-0"
              src={outlet.imageUrl}
              alt={outlet.title}
              size={OUTLET_LOGO_SIZE}
              fallbackToInitials
            />
            <div className="overflow-hidden">
              <div className="fw-bold text-truncate">
                <a href={outlet.href}>{outlet.title}</a>
              </div>
              {Number.isFinite(outlet.requestCount) && outlet.requestCount > 0 && (
                <div className="text-muted small text-truncate">{outlet.requestCount} requests</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

MediaOutletsRailCard.propTypes = {
  outlets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      href: PropTypes.string,
      imageUrl: PropTypes.string,
      title: PropTypes.string,
      requestCount: PropTypes.number,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
};

export default MediaOutletsRailCard;
